import React from 'react';
import { RECOVERY_ACTIONS } from './clerkRecoveryStates';
import './ClerkRecoveryScreen.css';

/**
 * Renders one resolved Clerk bridge recovery state (Slice F3).
 *
 * This component is intentionally dumb: it does not inspect `error_type` itself
 * and contains no per-code branching. All classification lives in
 * `clerkRecoveryStates.js` so the mapping is testable without rendering, and so
 * there is exactly one place to add a new backend error code.
 *
 * It never renders a legacy sign-in link, a registration link, or any raw error
 * detail from the server.
 */
function ClerkRecoveryScreen({ recovery, onRetry, onSignOut }) {
    if (!recovery || typeof recovery.errorType !== 'string') {
        throw new Error(
            'ClerkRecoveryScreen requires a resolved recovery state from resolveRecoveryState().'
        );
    }

    const { title, message, action, actionLabel, supportDestination } = recovery;

    let handler = null;
    if (action === RECOVERY_ACTIONS.RETRY) handler = onRetry;
    if (action === RECOVERY_ACTIONS.SIGN_OUT) handler = onSignOut;

    if ((action === RECOVERY_ACTIONS.RETRY || action === RECOVERY_ACTIONS.SIGN_OUT)
        && typeof handler !== 'function') {
        throw new Error(
            `ClerkRecoveryScreen received action "${action}" for "${recovery.errorType}" `
            + 'but no matching handler was provided.'
        );
    }

    return (
        <main
            className="clerk-recovery"
            role="alert"
            aria-label="Sign-in problem"
            data-error-type={recovery.errorType}
        >
            <div className="clerk-recovery__panel">
                <h1 className="clerk-recovery__title">{title}</h1>
                <p className="clerk-recovery__message">{message}</p>

                {handler && (
                    <button type="button" className="clerk-recovery__action" onClick={handler}>
                        {actionLabel}
                    </button>
                )}

                {action === RECOVERY_ACTIONS.SUPPORT && (
                    // The support destination is an owner decision that has not landed.
                    // Until it does, describe the next step rather than inventing a
                    // contact route or rendering a dead link.
                    supportDestination
                        ? (
                            <a className="clerk-recovery__action" href={supportDestination}>
                                {actionLabel}
                            </a>
                        )
                        : (
                            <p className="clerk-recovery__support-pending">
                                Please reach out to the Mythic team so we can sort this out
                                for you.
                            </p>
                        )
                )}

                {/*
                  * Sign-out is always offered as a secondary escape from a support
                  * state so the user is never trapped on a screen with no exit.
                  */}
                {action === RECOVERY_ACTIONS.SUPPORT && typeof onSignOut === 'function' && (
                    <button
                        type="button"
                        className="clerk-recovery__secondary"
                        onClick={onSignOut}
                    >
                        Sign out
                    </button>
                )}
            </div>
        </main>
    );
}

export default ClerkRecoveryScreen;
